import { create } from 'zustand';

import { useWsClientStore } from '@/services/ws-client';

import { BASE_WS_URL } from '@/constants/global-constants';

import { useAuthStore } from './authStore';

interface WebSocketState {
  isConnected: boolean;
  setIsConnected: (isConnected: boolean) => void;
  connect: (token: string) => Promise<void>;
  disconnect: () => void;
  reconnect: () => Promise<void>;
}

export const useWebSocketStore = create<WebSocketState>((set, get) => ({
  isConnected: false,
  setIsConnected: (isConnected: boolean) => set({ isConnected }),
  connect: async (token: string) => {
    // If already connected, do nothing:
    if (get().isConnected) return;

    // Define up to 3 attempts with delays of 1s, 3s, 10s:
    const retryDelays = [1000, 3000];

    for (let i = 0; i < retryDelays.length; i++) {
      const delay = retryDelays[i];
      try {
        await useWsClientStore.getState().initWebSocket(BASE_WS_URL, token);
        set({ isConnected: true });
        // If successful, we break out of the loop
        return Promise.resolve();
      } catch (error) {
        // Wait the appropriate delay before next attempt
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    useAuthStore.getState().signOut();
  },
  reconnect: async () => {
    const userState = useAuthStore.getState();
    if (!userState.token) {
      userState.signOut();
      return;
    }
    await get().connect(userState.token);
  },

  disconnect: () => {
    // If not connected, do nothing:
    if (!get().isConnected) return;

    try {
      useWsClientStore.getState().disconnect();
      set({ isConnected: false });
    } catch (error) {
      console.error('Failed to disconnect WS:', error);
    }
  },
}));
